import { useChatContext } from 'stream-chat-react';

import useGetUser from './useGetUser';

/**
 * Formats the last active time of a user into a human-readable relative time string.
 *
 * @param disabled - Optional Boolean flag to disable formatting and return undefined.
 *
 * @returns A string representing how long ago the user was active, or undefined if disabled.
 */
function useFormatLastActive(disabled: boolean = false): string | undefined {
	const { channel } = useChatContext();

	const user = useGetUser();

	if (channel.type === 'channel' || !channel.type || disabled) {
		return undefined;
	}

	// Check the other user role in channel (in case you need such functionality)
	// const isTeacherOrMentor = hasUserCorrectRole(user.user.lms_roles as Role[]);

	// if (isTeacherOrMentor) return undefined;

	const lastActive = user?.user?.last_active;

	if (!lastActive) return 'Unknown';

	const lastActiveDate = new Date(lastActive);
	const now = new Date();
	const diffInSeconds = Math.floor((now.getTime() - lastActiveDate.getTime()) / 1000);

	// Determine the largest time unit for the difference
	if (diffInSeconds < 60) {
		return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-diffInSeconds, 'seconds');
	}

	const diffInMinutes = Math.floor(diffInSeconds / 60);
	if (diffInMinutes < 60) {
		return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-diffInMinutes, 'minutes');
	}

	const diffInHours = Math.floor(diffInMinutes / 60);
	if (diffInHours < 24) {
		return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-diffInHours, 'hours');
	}

	const diffInDays = Math.floor(diffInHours / 24);
	if (diffInDays < 30) {
		return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-diffInDays, 'days');
	}

	const diffInMonths = Math.floor(diffInDays / 30);
	if (diffInMonths < 12) {
		return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-diffInMonths, 'months');
	}

	const diffInYears = Math.floor(diffInMonths / 12);
	return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-diffInYears, 'years');
}

export default useFormatLastActive;
